<template>
  <nav class="navbar navbar-expand-lg fixed-top">
    <a
      class="navbar-brand"
      :href="lenderData ? lenderData.lenderWebsiteUrl : '#'"
      :target="externalLinks ? '_blank' : undefined"
    >
      <span v-if="cuLogoUrl" class="cuscd-brand-logo-img-wrap">
        <img :src="cuLogoUrl" alt="Logo" class="icon-banner" />
      </span>
    </a>
    <router-link to="/">
      <span class="cuscd-brand-logo-name">SLC Applicant Portal</span>
    </router-link>

    <button
      type="button"
      class="navbar-toggler"
      aria-label="Toggle navigation"
      @click.prevent="toggleSidenav(true)"
    >
      <span class="navbar-toggler-icon"></span>
    </button>
    <a
      class="cuscd-notify-alert-mobile"
      title="Notifications"
      @click.prevent="showModal = true"
    >
      <span class="sr-only">Notifications</span>
      <span
        v-if="notificationCount > 0"
        class="cuscd-alert-count"
        id="alertBadge"
      >
        {{ notificationCount }}
      </span>
    </a>
    <div
      class="collapse navbar-collapse"
      :class="{ show: sidenavActive, collapsed: !sidenavActive }"
    >
      <ul class="navbar-nav mr-auto cuscd-right-side">
        <li class="nav-item active cuscd-title-bar" v-if="oidcIsAuthenticated">
          <span class="nav-link">
            <span class="cuscd-small-title">Welcome</span>
            <p class="cuscd-h1style" v-if="oidcIsAuthenticated">
              {{ decodeHTMLEncodedStr(formatName(oidcUser.nickname?.toLowerCase())) }}
            </p>
            <span class="sr-only">(current)</span>
            <a
              href
              class="close-btn"
              aria-label="Close"
              @click.prevent="toggleSidenav(false)"
            >
              &nbsp;
            </a>
          </span>
        </li>
        <li class="nav-item cuscd-solid-wrap" v-if="oidcIsAuthenticated">
          <router-link v-if="route.params.referenceId" :to="{name: 'ApplicationDocuments', params: { referenceId: `${route.params.referenceId}`}}" class="nav-link cuscd-mysummary">
            My Documents
          </router-link>
        </li>
        <li class="nav-item cuscd-solid-wrap" v-if="oidcIsAuthenticated">
          <router-link to="/" class="nav-link cuscd-mysummary">
            My Apps
          </router-link>
        </li>
        <li class="nav-item cuscd-solid-wrap" v-if="oidcIsAuthenticated">
          <a
            href=""
            class="nav-link cuscd-logout"
            @click.prevent="showLogoutModal = true"
          >
            Logout
          </a>
        </li>
        <li class="nav-item cuscd-solid-wrap" v-if="!oidcIsAuthenticated">
          <a href="" class="nav-link cuscd-logout" @click.prevent="login">
            Login
          </a>
        </li>
      </ul>
    </div>
  </nav>
  <LogoutModal
    v-if="showLogoutModal"
    @close="showLogoutModal = false"
  ></LogoutModal>
</template>

<script lang="ts">
import { mapState, mapGetters, mapActions } from "vuex";
import { computed, defineComponent } from "vue";
import { useRoute } from "vue-router";
import LogoutModal from "../../modals/LogoutModal.vue";
import useHtmlUtilities from "@/use/htmlUtilities";
import useFormatter from "@/use/formatter";

export default defineComponent({
  props: {
    externalLinks: {
      type: Boolean,
      default: false,
    },
  },
  name: "StudentChoiceHeader",
  setup(){
    const route = useRoute();
    const { decodeHTMLEncodedStr } = useHtmlUtilities();
    const { formatName } = useFormatter();
    return { decodeHTMLEncodedStr, route, formatName }
  },
  components: { LogoutModal },
  data: () => ({
    sidenavActive: false,
    showModal: false,
    showLogoutModal: false,
  }),
  computed: {
    ...mapState([
      "preferredName",
      "notifications",
      "profile",
      "lenderData",
      "configuration",
    ]),
    ...mapGetters(["cuLogoUrl"]),
    ...mapGetters("oidcStore", ["oidcIsAuthenticated", "oidcUser"]),
    notificationCount(): number {
      return (this.notifications || []).length as number;
    },
  },
  methods: {
    ...mapActions(["setNotifications", "setProfile", "setLenderData"]),
    ...mapActions("oidcStore", [
      "authenticateOidc",
      "removeOidcUser",
      "signOutOidcSilent",
    ]),
    login: function () {
      this.authenticateOidc();
    },
    toggleSidenav: function (open: boolean) {
      this.sidenavActive = open;
    },
  },
});
</script>

<style scoped>
.cuscd-wrap .navbar {
  background: -moz-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  background: -webkit-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#66000000', endColorstr='#00000000',GradientType=1 );
  background-color: var(--theme-color);
  border-color: #080808;
  height: 5.625rem;
  padding: 0 1rem 0 0;
  position: relative;
}

.cuscd-dark .navbar {
  background: -moz-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  background: -webkit-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#66ffffff', endColorstr='#00ffffff',GradientType=1 );
  background-color: var(--theme-color);
}

.cuscd-brand-logo-name {
  display: none;
}

.cuscd-wrap .navbar-toggler {
  left: 0rem;
  position: absolute;
}

/* TODO: Trying to animate sidenav */
.cuscd-wrap .navbar-collapse.collapse.collapsed {
  display: none;
  /* transform: translateX(-100%);
  transition: transform 150ms ease-in 0s; */
}

/* .cuscd-wrap .navbar-collapse.collapse.show {
  transition: transform 0.2s ease;
} */

.cuscd-wrap .navbar-toggler-icon {
  background: no-repeat center center;
  background-size: 100% 100%;
  content: "";
  display: inline-block;
  height: 1.5em;
  vertical-align: middle;
  width: 1.8em;
}
.cuscd-wrap .navbar-dark .navbar-toggler-icon {
  background-image: url("~@/assets/images/toggler-btn.png");
}
.cuscd-right-side {
  display: block;
  height: 100%;
  left: 0;
  padding: 0;
  position: fixed;
  top: 2rem;
}
.cuscd-wrap .navbar-nav li.nav-item.dropdown {
  display: none;
}
.cuscd-right-side .close-btn {
  display: none;
}
.cuscd-wrap .navbar-brand {
  display: block;
  height: 100%;
  left: 50%;
  margin: 0;
  max-width: 12rem;
  padding: 0.25rem 0.5rem;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
}

.cuscd-brand-logo-img-wrap {
  background: #fff;
  display: inline-block;
  font-size: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  text-align: center;
  vertical-align: top;
  width: 100%;
}
.cuscd-brand-logo-img-wrap img {
  bottom: 0;
  display: block;
  left: 0;
  margin: auto;
  max-height: 100%;
  max-width: 100%;
  position: absolute;
  right: 0;
  top: 0;
}
.cuscd-wrap .navbar-nav li {
  display: block;
  position: relative;
}
.cuscd-desktop-only {
  display: block;
}
.cuscd-notify-alert-mobile {
  color: #d9d9d9;
  display: none;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.5rem;
  pointer-events: auto;
  position: absolute;
  right: 1.8rem;
  text-decoration: none;
  top: 0.6rem;
  vertical-align: top;
}
.cuscd-notify-alert-mobile .cuscd-alert-count {
  background: #bb0000;
  border-radius: 1em;
  color: #ffffff;
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  left: 50%;
  line-height: 1.4em;
  margin: 0 0 0 0em;
  min-width: 1.563rem;
  padding: 0.05rem 0.5em;
  position: absolute;
  text-align: center;
  top: 0.367rem;
  z-index: 1;
}
.cuscd-overlay,
.cuscd-banner-text {
  display: none;
}
.cuscd-small-title {
  display: block;
  font-size: 0.75rem;
  position: relative;
  right: 0;
  text-align: right;
  top: 0;
}
.cuscd-small-title-left {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  left: 0;
  margin: 0 0 0.5rem 0;
  position: relative;
  text-align: left;
  top: 0;
}
.cuscd-profile::before,
.cuscd-profile:before {
  background: url("~@/assets/images/Myprofile-rev.png") no-repeat center;
  background-size: contain;
  content: "";
  display: block;
  height: 18px;
  left: 0.6rem;
  position: absolute;
  top: 2.15rem;
  width: 14px;
}
.cuscd-mysummary::before,
.cuscd-mysummary:before {
  background: url("~@/assets/images/mysummary-rev.png") no-repeat center;
  background-size: contain;
  content: "";
  display: block;
  height: 18px;
  left: 0.6rem;
  position: absolute;
  top: 2.15rem;
  width: 14px;
}
.cuscd-logout::before,
.cuscd-logout:before {
  background: url("~@/assets/images/Logout-rev.png") no-repeat center;
  background-size: contain;
  content: "";
  display: block;
  height: 18px;
  left: 0.8rem;
  position: absolute;
  top: 2.15rem;
  width: 17px;
}
.cuscd-close-img {
  width: 1.125rem;
}
.cuscd-full-wrap a:hover,
.cuscd-full-wrap a:focus {
  filter: none;
}

@media (min-width: 576px) {
  .cuscd-brand-logo-name {
    display: none;
  }

  .cuscd-wrap .navbar-toggler {
    left: 1rem;
    position: absolute;
  }

  .cuscd-wrap .navbar-collapse.collapse.show {
    display: block;
    position: relative;
  }

  .cuscd-wrap .navbar-toggler-icon {
    background: no-repeat center center;
    background-size: 100% 100%;
    content: "";
    display: inline-block;
    height: 1.031rem;
    vertical-align: middle;
    width: 1.469rem;
  }

  .cuscd-wrap .navbar-nav li {
    display: block;
    position: relative;
  }
}

@media (min-width: 992px) {
  .cuscd-wrap .navbar-expand-md .navbar-toggler {
    display: none;
  }
  .cuscd-wrap .navbar-brand {
    background: #fff;
    display: inline-block;
    left: 0;
    padding: 0.5rem 1rem;
    position: relative;
    transform: none;
    width: 11.125rem;
  }
  .cuscd-brand-logo-img-wrap {
    vertical-align: middle;
  }

  .cuscd-wrap .navbar-collapse.collapse.show {
    position: initial;
  }

  .cuscd-right-side {
    background: transparent;
    display: block;
    left: auto;
    padding-right: 2.5rem;
    /*right: .8rem;*/
    right: 0; /* makes nav stay to the right*/
    top: 0;
    /*width: 38rem;*/ /* remove the width to allow for whole nav to show*/
    position: absolute;
  }
  .cuscd-right-side.show {
    /* prevents navbar links from being off the x-y axis */
    position: absolute;
  }
  .cuscd-brand-logo-name {
    display: inline-block;
  }
  .cuscd-wrap .navbar-nav li {
    display: table-cell;
    position: relative;
    vertical-align: middle;
  }
  .cuscd-wrap .navbar-nav .nav-item .nav-link {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1rem;
    padding: 2.25rem 2.5rem;
  }
  .cuscd-solid-wrap {
    white-space: nowrap;
  }
  .cuscd-wrap .navbar .nav-item.cuscd-title-bar .nav-link {
    color: #ffffff;
    font-size: 1.125rem;
    font-weight: 700;
    font-style: normal;
    line-height: 1.15rem;
    padding: 1.1rem 2.5rem 1.1rem 2.5rem;
    vertical-align: middle;
  }
  .cuscd-wrap .navbar .nav-item.cuscd-title-bar .nav-link .small-title {
    font-size: 0.75rem;
    font-weight: 400;
  }
  .cuscd-wrap .navbar-dark .navbar-nav .nav-link {
    color: #ffffff;
    opacity: 1;
  }
  .cuscd-wrap .navbar-dark .navbar-nav .nav-link:hover,
  .cuscd-wrap .navbar-dark .navbar-nav .nav-link:focus {
    opacity: 0.8;
  }
  .cuscd-wrap .navbar-nav li {
    display: table-cell;
    position: relative;
    vertical-align: middle;
  }
  .cuscd-wrap .navbar-nav li a {
    padding: 1em 1.5em;
  }
  .cuscd-wrap .navbar-nav a.brand-logo {
    color: #ffffff;
    text-decoration: none;
  }
  .cuscd-wrap .navbar-nav a.brand-logo:hover,
  .cuscd-wrap .navbar-nav a.brand-logo:focus {
    color: #e1e1e1;
    text-decoration: underline;
  }
  .cuscd-brand-logo img {
    display: inline-block;
    position: relative;
    z-index: 2;
  }
  .cuscd-brand-logo-name {
    color: #ffffff;
    display: inline-block;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    padding: 0.3rem 0 0 1.8rem;
    position: relative;
    vertical-align: middle;
    width: 146px;
  }

  /* DARK */
  .cuscd-dark .cuscd-brand-logo-name,
  .cuscd-dark .cuscd-wrap .navbar .nav-item.cuscd-title-bar .nav-link,
  .cuscd-dark .cuscd-wrap a.nav-link,
  .cuscd-dark .cuscd-wrap .btn-block.cuscd-button-color-cta {
    color: #000100;
  }

  .cuscd-dark .cuscd-profile::before,
  .cuscd-profile:before {
    background: url("~@/assets/images/Myprofile.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 18px;
    left: 0.6rem;
    position: absolute;
    top: 2.15rem;
    width: 14px;
  }
  .cuscd-dark .cuscd-mysummary::before,
  .cuscd-mysummary:before {
    background: url("~@/assets/images/mysummary.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 18px;
    left: 0.6rem;
    position: absolute;
    top: 2.15rem;
    width: 14px;
  }
  .cuscd-dark .cuscd-logout::before,
  .cuscd-logout:before {
    background: url("~@/assets/images/Logout.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 18px;
    left: 0.8rem;
    position: absolute;
    top: 2.15rem;
    width: 17px;
  }
  .cuscd-profile::before,
  .cuscd-profile:before {
    background: url("~@/assets/images/Myprofile-rev.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 18px;
    left: 0.6rem;
    position: absolute;
    top: 2.15rem;
    width: 14px;
  }
  .cuscd-mysummary::before,
  .cuscd-mysummary:before {
    background: url("~@/assets/images/mysummary-rev.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 18px;
    left: 0.6rem;
    position: absolute;
    top: 2.15rem;
    width: 14px;
  }
  .cuscd-logout::before,
  .cuscd-logout:before {
    background: url("~@/assets/images/Logout-rev.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 18px;
    left: 0.8rem;
    position: absolute;
    top: 2.15rem;
    width: 17px;
  }
}

@media (max-width: 1200px) {
  .cuscd-wrap .navbar-nav .nav-item .nav-link {
    padding: 2.25rem 0.5rem 2.25rem 2rem;
  }
}

@media (max-width: 992px) {
  .cuscd-full-wrap .cuscd-wrap .navbar {
    background: linear-gradient(
      315deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 47%,
      rgba(255, 255, 255, 0.4) 88%
    );
    background-color: #ffffff;
    border-color: #080808;
    box-shadow: 8px 16px 22px rgba(0, 0, 0, 0.11);
    height: 2.688rem;
    left: 0;
    padding: 0 1rem 0 0;
    position: fixed;
    top: 0;
  }
  .cuscd-dark .cuscd-wrap .navbar {
    background-color: #fff;
  }
  .cuscd-wrap .navbar-toggler {
    padding: 0.25rem 0.85rem;
    top: 0.2rem;
  }
  .cuscd-wrap .navbar-toggler-icon {
    background: url("~@/assets/images/toggler-btn.png") no-repeat center center;
    background-color: var(--theme-color);
    background-size: cover;
    content: "";
    display: inline-block;
    height: 1.031rem;
    vertical-align: middle;
    width: 1.469rem;
  }

  .cuscd-dark .cuscd-wrap .navbar-toggler-icon {
    background-color: #6f6f6f;
  }
  .cuscd-brand-logo-img-wrap img {
    position: relative;
  }
  .cuscd-notify-alert-mobile {
    top: 0.1rem;
    display: block;
  }
  .cuscd-dark .cuscd-wrap .navbar .cuscd-notify-alert-mobile::after,
  .cuscd-dark .cuscd-wrap .navbar .cuscd-notify-alert-mobile:after {
    background-color: #6f6f6f;
  }

  .cuscd-wrap .navbar-nav li {
    padding-left: 1.8rem;
    padding-right: 1.8rem;
  }
  .cuscd-wrap .navbar-nav li.cuscd-title-bar {
    padding-left: 1.8rem;
  }

  .cuscd-mysummary::before,
  .cuscd-mysummary:before,
  .cuscd-profile::before,
  .cuscd-profile:before,
  .cuscd-mydocs::before,
  .cuscd-mydocs:before,
  .cuscd-support::before,
  .cuscd-support:before,
  .cuscd-logout::before,
  .cuscd-logout:before {
    height: 1.156rem;
    left: 1.8rem;
    top: 0.6rem;
    width: 1.156rem;
  }
  .cuscd-wrap .navbar-nav li .cuscd-mysummary,
  .cuscd-wrap .navbar-nav li .cuscd-notify,
  .cuscd-wrap .navbar-nav li .cuscd-logout,
  .cuscd-wrap .navbar-nav li .cuscd-support,
  .cuscd-wrap .navbar-nav li .cuscd-profile,
  .cuscd-wrap .navbar-nav li .cuscd-mydocs {
    font-size: 1rem;
    padding-left: 2.1rem;
    padding-top: 0.6rem;
  }
  .cuscd-notify .cuscd-alert-count {
    background: #ffffff;
    border-radius: 1em;
    color: #383838;
    display: block;
    font-size: 0.75em;
    font-weight: 500;
    left: 10.3rem;
    line-height: 1.5em;
    margin: 0;
    min-width: 2.1em;
    padding: 0.05rem 0.5em;
    position: absolute;
    text-align: center;
    top: 0.7rem;
    z-index: 1;
  }
  .cuscd-wrap .navbar-dark .navbar-nav .nav-link {
    color: rgba(255, 255, 255, 1);
  }
  .cuscd-wrap .navbar-collapse.collapse {
    transition: 0.25s ease-in-out;
  }
  .cuscd-right-side {
    background: var(--theme-color);
    color: #ffffff;
    display: block;
    height: 100%;
    left: 0;
    padding: 0;
    position: fixed;
    top: 0;
    width: 85%;
  }
  .cuscd-title-bar {
    background: -moz-linear-gradient(
      top,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.1) 100%
    );
    background: -webkit-linear-gradient(
      top,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.1) 100%
    );
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.1) 100%
    );
    background-color: var(--theme-color);
    border-color: #080808;
    display: block;
    font-size: 1.125rem;
    font-weight: 700;
    height: 4.5rem;
    line-height: 1.1rem;
    margin-bottom: 1.5rem;
    padding: 0.5rem 1rem;
    position: relative;
  }
  .cuscd-dark .cuscd-title-bar {
    background: -moz-linear-gradient(
      top,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 100%
    );
    background: -webkit-linear-gradient(
      top,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 100%
    );
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 100%
    );
  }
  .cuscd-wrap .close-btn {
    display: inline-block;
    height: 26px;
    pointer-events: auto;
    position: absolute;
    right: 0.3rem;
    top: 0.9rem;
    width: 26px;
  }
  .cuscd-wrap .close-btn::before,
  .cuscd-wrap .close-btn:before {
    background: url("~@/assets/images/close.png") no-repeat center;
    background-size: contain;
    display: inline-block;
    content: "";
    height: 0.894rem;
    left: 0;
    position: absolute;
    top: 0;
    width: 0.825rem;
    z-index: 3;
  }

  .cuscd-small-title {
    font-size: 0.75rem;
    font-weight: 700;
    text-align: left;
  }

  /* DARK */
  .cuscd-dark .cuscd-right-side,
  .cuscd-dark .cuscd-wrap a.nav-link {
    color: #000100;
  }
  .cuscd-dark .cuscd-wrap .close-btn::before,
  .cuscd-dark .cuscd-wrap .close-btn:before {
    background: url("~@/assets/images/close-dark.png") no-repeat center;
    background-size: contain;
    display: inline-block;
    content: "";
    height: 0.894rem;
    left: 0;
    position: absolute;
    top: 0;
    width: 0.825rem;
    z-index: 3;
  }
  .cuscd-dark .cuscd-profile::before,
  .cuscd-profile:before {
    background: url("~@/assets/images/Myprofile.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 1.156rem;
    left: 1.8rem;
    position: absolute;
    top: 0.6rem;
    width: 1.156rem;
  }
  .cuscd-dark .cuscd-mysummary::before,
  .cuscd-mysummary:before {
    background: url("~@/assets/images/mysummary.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 1.156rem;
    left: 1.8rem;
    position: absolute;
    top: 0.6rem;
    width: 1.156rem;
  }
  .cuscd-dark .cuscd-logout::before,
  .cuscd-logout:before {
    background: url("~@/assets/images/Logout.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 1.156rem;
    left: 1.8rem;
    position: absolute;
    top: 0.6rem;
    width: 1.156rem;
  }

  .cuscd-profile::before,
  .cuscd-profile:before {
    background: url("~@/assets/images/Myprofile-rev.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 1.156rem;
    left: 1.8rem;
    position: absolute;
    top: 0.6rem;
    width: 1.156rem;
  }
  .cuscd-mysummary::before,
  .cuscd-mysummary:before {
    background: url("~@/assets/images/mysummary-rev.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 1.156rem;
    left: 1.8rem;
    position: absolute;
    top: 0.6rem;
    width: 1.156rem;
  }
  .cuscd-logout::before,
  .cuscd-logout:before {
    background: url("~@/assets/images/Logout-rev.png") no-repeat center;
    background-size: contain;
    content: "";
    display: block;
    height: 1.156rem;
    left: 1.8rem;
    position: absolute;
    top: 0.6rem;
    width: 1.156rem;
  }

  .cuscd-wrap .navbar-nav .nav-item .nav-link {
    padding: 0.55rem 1.5rem 2.25rem;
  }
  .cuscd-wrap .navbar-nav .nav-item .nav-link:hover {
    cursor: pointer;
  }
}
</style>
